import { Link } from "@/lib/router-compat";
import { getRouteApi } from "@tanstack/react-router";
import { ArrowRight, Palette, Ruler, Camera, HardHat } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import TextReveal from "@/components/TextReveal";
import MagneticButton from "@/components/MagneticButton";
import ParallaxImage from "@/components/ParallaxImage";
import OptimizedImage, { optimizedSrc, buildSrcSet } from "@/components/OptimizedImage";
import Testimonials from "@/components/Testimonials";
import type { FeaturedProject } from "@/lib/home-content.functions";

import heroImg from "@/assets/hero-interior.jpg";

const routeApi = getRouteApi("/");

const services = [
  { icon: Ruler, title: "Concept & Aménagement", desc: "Plans, cloisons à déplacer, circulations à revoir, rangements à intégrer. Je dessine l'espace jusqu'aux cotes que les artisans utiliseront sur le chantier." },
  { icon: Palette, title: "Décoration & Colorimétrie", desc: "Un Master en couleur en poche, les teintes se testent chez vous, à midi et le soir. Un vert qui fonctionne plein sud vire au gris dans un salon orienté nord." },
  { icon: Camera, title: "Images 3D photoréalistes", desc: "Vous voyez la pièce finie avant de commander quoi que ce soit. C'est souvent là qu'on ajuste la taille d'un îlot ou la couleur d'une façade de cuisine." },
  { icon: HardHat, title: "Suivi de chantier", desc: "Je passe sur place, je relis les devis, je réponds aux artisans quand une question se pose. Vous n'avez pas à trancher seul quand deux corps de métier ne sont pas d'accord." },
];

const Index = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const loaderData = routeApi.useLoaderData();
  const heroSrc: string = loaderData?.heroImage ?? heroImg;
  const featuredProjects: FeaturedProject[] = loaderData?.featuredProjects ?? [];

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <Layout>
      <SEOHead
        title="JIIA Studio · Architecte d'intérieur & Designer de concept"
        description="Architecte d'intérieur & spécialiste couleur. Concept, mobilier sur mesure, identité forte. Les espaces dont on se souvient, à Lyon et Rhône-Alpes."
        canonical="/"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "JIIA Studio",
          description: "Cabinet d'architecture d'intérieur à Grézieu-la-Varenne, Ouest Lyon",
          url: "https://jiia-studio.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Grézieu-la-Varenne",
            addressRegion: "Auvergne-Rhône-Alpes",
            addressCountry: "FR",
          },
           founder: { "@type": "Person", name: "Charline Jacob" },
           areaServed: "Région Rhône-Alpes",
           priceRange: "€€",
        }, {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "JIIA Studio",
          url: "https://jiia-studio.com",
        }]}
      />

      {/* Hero with parallax */}
      <section ref={heroRef} className="relative h-[90vh] flex items-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
               src={optimizedSrc(heroSrc, 1280)}
               srcSet={buildSrcSet(heroSrc, 1280)}
               sizes="100vw"
               alt="Intérieur design contemporain par JIIA Studio"
               className="w-full h-full object-cover object-center scale-110"
               loading="eager"
               decoding="sync"
               fetchPriority="high"
               width={1280}
               height={720}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-purple/80 via-dark-purple/25 to-transparent" />
        </motion.div>
        <motion.div className="relative container pb-16 md:pb-24" style={{ opacity: heroOpacity }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <TextReveal delay={0.4}>
              <h1 className="text-primary-foreground/70 text-xs sm:text-sm font-body uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4">
                 Studio d'architecture d'intérieur à Lyon et Rhône-Alpes
               </h1>
            </TextReveal>
            <TextReveal delay={0.6}>
              <p className="font-heading text-3xl sm:text-4xl md:text-6xl lg:text-7xl text-primary-foreground font-light leading-tight max-w-3xl">
                L'espace que vous imaginez.
              </p>
            </TextReveal>
            <TextReveal delay={0.85}>
              <span className="block font-heading text-3xl sm:text-4xl md:text-6xl lg:text-7xl text-primary-foreground font-light leading-tight max-w-3xl italic">
                Et celui que vous n'osiez pas imaginer.
              </span>
            </TextReveal>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.1, ease: "easeOut" }}
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 mt-8 text-primary-foreground text-sm font-body uppercase tracking-widest hover:gap-5 transition-all"
                >
                  Discutons de votre projet <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Intro */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <AnimatedSection direction="none" scale>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-6">
              JIIA Studio
            </p>
          </AnimatedSection>
          <TextReveal delay={0.1}>
            <h2 className="font-heading text-3xl md:text-4xl font-light leading-relaxed mb-6">
              Un plan qui ne suffit pas. Une image qui ne ment pas.
            </h2>
          </TextReveal>
          <AnimatedSection delay={0.3}>
            <p className="text-muted-foreground font-body leading-relaxed text-base font-normal whitespace-pre-line">
              Je suis Charline Jacob, architecte d'intérieur près de Lyon. Vous rénovez votre appartement ou votre maison, ou vous ouvrez un commerce, un cabinet. Je relève l'existant, j'analyse les contraintes et je dessine votre projet. Vous voyez votre pièce finie en 3D avant de valider quoi que ce soit. Et c'est souvent là que vous changez un plan de travail ou déplacez une verrière. Je reste ensuite jusqu'aux finitions, pour que vous n'ayez jamais à arbitrer seul entre le carreleur et l'électricien.
            </p>
            <MagneticButton className="inline-block">
              <Link
                to="/a-propos"
                className="inline-flex items-center gap-2 mt-8 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
              >
                Découvrir le studio <ArrowRight className="h-4 w-4" />
              </Link>
            </MagneticButton>
          </AnimatedSection>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-secondary">
        <div className="container">
          <AnimatedSection direction="none" scale>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4 text-center">
              Prestations
            </p>
          </AnimatedSection>
          <TextReveal delay={0.1}>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-center mb-16">
              Ce que je crée pour vous
            </h2>
          </TextReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 0.1} direction={i % 2 === 0 ? "left" : "right"}>
                <motion.div
                  whileHover={{ y: -8, transition: { duration: 0.3, ease: "easeOut" } }}
                  className="p-8 bg-background rounded-sm hover:shadow-xl transition-shadow duration-500 h-full flex flex-col"
                >
                  <div>
                    <service.icon className="h-6 w-6 text-accent mb-4" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl mb-2">{service.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed flex-1">{service.desc}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
          <div className="text-center mt-12">
            <MagneticButton className="inline-block">
              <Link
                to="/prestations"
                className="inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
              >
                Toutes les prestations <ArrowRight className="h-4 w-4" />
              </Link>
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section className="py-24 md:py-32">
        <div className="container">
          <AnimatedSection direction="left">
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">
              Projets
            </p>
            <div className="flex items-end justify-between mb-16">
              <TextReveal>
                <h2 className="font-heading text-3xl md:text-4xl font-light">
                  Réalisations récentes
                </h2>
              </TextReveal>
              <MagneticButton>
                <Link
                  to="/projets"
                  className="hidden md:inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
                >
                  Voir tout <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredProjects.map((project, i) => (
              <AnimatedSection key={project.id} delay={i * 0.15}>
                <Link to={`/projets/${project.slug}`} className="group block">
                  <div className="aspect-[3/4] overflow-hidden rounded-sm mb-4">
                    {project.cover_image ? (
                      <motion.img
                        src={optimizedSrc(project.cover_image, 500)}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full bg-secondary flex items-center justify-center">
                        <p className="text-muted-foreground text-xs font-body">Image à venir</p>
                      </div>
                    )}
                  </div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.15 }}
                  >
                    <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">
                      {project.category}
                    </p>
                    <h3 className="font-heading text-xl">{project.title}</h3>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
          <div className="text-center mt-8 md:hidden">
            <Link
              to="/projets"
              className="inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
            >
              Voir tout <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* SEO internal links */}
      <section className="py-20 md:py-24 bg-secondary/50">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4 text-center">
              Mes expertises
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-center mb-12">
              Des espaces pensés pour marquer
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Rénovation intérieure", href: "/renovation-interieure-lyon", desc: "Appartements haussmanniens, maisons des années 70, combles perdus.\nPlans, matériaux, chantier." },
              { label: "Aménagement professionnel", href: "/amenagement-professionnel-hotellerie", desc: "Restaurants, hôtels, cabinets, boutiques.\nCirculations, normes, ambiance." },
              { label: "Images 3D", href: "/images-3d-visualisation-architecturale", desc: "Des rendus pour trancher entre deux options.\nOu convaincre une banque." },
              { label: "Décoration & Colorimétrie", href: "/decoration-interieure-lyon", desc: "Teintes testées sur vos murs, mobilier, lumière.\nSans casser de cloison." },
            ].map((link, i) => (
              <AnimatedSection key={link.href} delay={i * 0.1}>
                <Link
                  to={link.href}
                  className="group block p-6 bg-background rounded-sm border border-border hover:border-accent/50 transition-colors h-full"
                >
                  <h3 className="font-heading text-lg mb-2 group-hover:text-accent transition-colors">{link.label}</h3>
                  <p className="text-xs text-muted-foreground font-body leading-relaxed whitespace-pre-line">{link.desc}</p>
                  <ArrowRight className="h-4 w-4 mt-3 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground overflow-hidden">
        <div className="container text-center max-w-2xl">
          <AnimatedSection direction="none" scale>
            <TextReveal>
              <h2 className="font-heading text-3xl md:text-5xl font-light mb-6">
                Un projet en tête, même flou ?
              </h2>
            </TextReveal>
            <AnimatedSection delay={0.2}>
              <p className="text-primary-foreground/70 font-body mb-8">
                Le premier rendez-vous est offert et je réponds sous 48h.
              </p>
            </AnimatedSection>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
            >
              <MagneticButton className="inline-block">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 px-8 py-4 border border-primary-foreground/30 text-sm font-body uppercase tracking-widest hover:bg-primary-foreground hover:text-primary transition-all duration-500"
                >
                  Prendre rendez-vous <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
